import axios, { AxiosInstance } from 'axios';
import { InteractionRequiredAuthError, IPublicClientApplication } from '@azure/msal-browser';
import { msalConfig } from '../config/msalConfig';

export const createAxiosInstance = (
  msalInstance: IPublicClientApplication,
  authority?: string
): AxiosInstance => {
  const axiosInstance = axios.create({
    baseURL: process.env.REACT_APP_PORTAL_API,
  });

  // Cache token promise to prevent multiple simultaneous token requests
  let tokenAcquisitionPromise: Promise<string | null> | null = null;

  const getAccessToken = async () => {
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length === 0) {
      await msalInstance.loginRedirect({
        scopes: msalConfig.auth.scopes,
        authority,
      });
      return null;
    }

    const tokenRequest = {
      scopes: msalConfig.auth.scopes,
      account: accounts[0],
      authority,
    };
    try {
      const response = await msalInstance.acquireTokenSilent(tokenRequest);
      return response.accessToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        await msalInstance.acquireTokenRedirect(tokenRequest);
        return null;
      }
      throw error;
    }
  };

  axiosInstance.interceptors.request.use(
    async (config) => {
      try {
        if (!tokenAcquisitionPromise) {
          tokenAcquisitionPromise = getAccessToken();
        }
        const token = await tokenAcquisitionPromise;
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      } catch (error) {
        console.error('Token acquisition failed:', error);
        return Promise.reject(error);
      } finally {
        tokenAcquisitionPromise = null;
      }
    }
  );

  // Add response interceptor to handle 401 errors
  axiosInstance.interceptors.response.use(
    response => response,
    async (error) => {
      if (error.response?.status === 401) {
        tokenAcquisitionPromise = null;
      }
      return Promise.reject(error);
    }
  );

  return axiosInstance;
};
